import { useCollection, useStore } from '@dovefiniscono/sync/react';
import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { today as todayISO } from '../lib/dates';
import { DEFAULT_POINTS } from './challenges';
import {
  challengeIdOfGoal,
  computeGame,
  type Game,
  type WeeklyGoal,
  weeklyGoalTitle,
} from './game';
import { usePreferences } from './preferences';

const GameContext = createContext<Game | null>(null);

/** Computes the game once for the whole app, from the synced collections. */
export function GameProvider({ children }: { children: ReactNode }) {
  const store = useStore();
  const sessions = useCollection('focusSessions');
  const tasks = useCollection('tasks');
  const checkins = useCollection('taskCheckins');
  const events = useCollection('events');
  const transactions = useCollection('transactions');
  const challenges = useCollection('challenges');
  const [preferences, save] = usePreferences();
  const today = todayISO();

  // The weekly goals set before challenges existed become challenges of yours, once: the same id
  // on every device, so two devices doing it at once make one.
  useEffect(() => {
    const goals = preferences.weeklyGoals as WeeklyGoal[];
    if (goals.length === 0) return;
    for (const goal of goals) {
      store.create(
        'challenges',
        {
          title: weeklyGoalTitle(goal),
          period: 'week',
          metric: goal.metric,
          target: goal.target,
          points: DEFAULT_POINTS.week,
          weekdays: null,
          doneDays: [],
          since: goal.since,
          archivedAt: null,
        },
        challengeIdOfGoal(goal.id),
      );
    }
    save({ weeklyGoals: [] });
  }, [preferences.weeklyGoals, store, save]);

  const game = useMemo(
    () =>
      computeGame({ sessions, tasks, checkins, events, transactions, challenges }, today, {
        restWeekdays: preferences.restWeekdays,
        restDays: preferences.restDays,
        weeklyGoals: preferences.weeklyGoals as WeeklyGoal[],
        reviewedWeeks: preferences.reviewedWeeks,
      }),
    [sessions, tasks, checkins, events, transactions, challenges, today, preferences],
  );
  return createElement(GameContext.Provider, { value: game }, children);
}

export function useGame(): Game {
  const game = useContext(GameContext);
  if (!game) throw new Error('useGame must be used inside <GameProvider>');
  return game;
}
